import { useEffect, useMemo, useState } from "react";
import { collection, doc, getDocs, limit, query, where } from "firebase/firestore";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { db } from "@/services/firebaseService";
import { updateCampLocation } from "@/services/campLocationUpdateService";
import { useAuth } from "@/context/AuthContext";
import { isValidCampSlugSegment, normalizeCampSlugSegment } from "@/utils/campSlugUtils";

export function EditLocationSlugDialog({
  open,
  onOpenChange,
  locationId,
  orgId,
  initialSlug,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  locationId: string;
  orgId: string;
  initialSlug?: string;
  onSaved?: () => void;
}) {
  const { currentUser } = useAuth();
  const [value, setValue] = useState(initialSlug ?? "");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) setValue(initialSlug ?? "");
  }, [open, initialSlug]);

  const initialTrimmed = useMemo(
    () => (typeof initialSlug === "string" ? initialSlug.trim() : ""),
    [initialSlug]
  );
  const trimmed = useMemo(() => value.trim(), [value]);
  const normalized = useMemo(() => normalizeCampSlugSegment(trimmed), [trimmed]);
  const isChangingNonEmptyExisting = initialTrimmed.length > 0 && normalized !== normalizeCampSlugSegment(initialTrimmed);

  const checkSlugUniqueInOrg = async (candidate: string): Promise<boolean> => {
    if (!candidate) return true;
    const orgRef = doc(db, "campOrganizations", orgId);
    const q = query(
      collection(db, "campLocations"),
      where("providedByOrg", "==", orgRef),
      where("slug", "==", candidate),
      limit(5)
    );
    const snap = await getDocs(q);
    return snap.docs.every((d) => d.id === locationId);
  };

  const handleSave = async () => {
    if (!currentUser?.uid) {
      toast.error("Not authenticated");
      return;
    }

    setIsSaving(true);
    try {
      if (normalized && !isValidCampSlugSegment(normalized)) {
        toast.error("Use lowercase letters, numbers, and hyphens only (no underscores; max 80 characters).");
        return;
      }

      if (normalized) {
        const isUnique = await checkSlugUniqueInOrg(normalized);
        if (!isUnique) {
          toast.error("This slug is already used by another location in your organization.");
          return;
        }
      }

      await updateCampLocation(
        db,
        locationId,
        { slug: normalized },
        currentUser.uid,
        "Update location public URL slug",
        true
      );
      toast.success(normalized ? "Location URL slug updated" : "Location URL slug cleared");
      onSaved?.();
      onOpenChange(false);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Failed to update slug";
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Edit location URL slug</DialogTitle>
        </DialogHeader>

        {isChangingNonEmptyExisting && (
          <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900">
            <strong>Warning!</strong> Changing this slug will break existing links and bookmarks to this location on the
            public site until search engines and partners pick up the new address.
          </div>
        )}

        <div className="space-y-2">
          <div className="text-sm text-muted-foreground">Slug</div>
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="e.g. raleigh-swim-school"
            autoFocus
          />
          <div className="text-xs text-muted-foreground">
            Shown in public web addresses for this location. Underscores and spaces are saved as hyphens (underscores
            are reserved between org and location segments). Must be unique within your organization. Leave blank to clear.
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
